import { Controller } from "@hotwired/stimulus"

// Keeps a <details> disclosure honest, and closes it the way a menu closes.
//
// Two jobs.
//
// First, the ported UI::DropdownComponent emits Primer's markup, including a
// static `aria-expanded="false"` that upstream's `details-toggle` custom
// element maintains. We do not load that element, so without this the
// attribute says "collapsed" while the menu is open, which contradicts what
// the native element announces and is worse than having said nothing.
//
// Only that one attribute. The accessible name is deliberately left alone:
// swapping it to "Close sign in menu" on open is what upstream does, and it
// breaks WCAG 2.5.3 Label in Name by removing the visible label from the name.
// Open state belongs in aria-expanded, not in the name.
//
// Second, dismissal. A previous version of this file claimed Escape and
// clicking outside were native to <details>. They are not: that is <dialog>
// and the popover API. A <details> used as a menu stays open until its own
// summary is clicked again, so opening one and clicking elsewhere on the page
// left it hanging over the content. Both are implemented here.
export default class extends Controller {
  static targets = ["summary"]

  connect() {
    this.sync = this.sync.bind(this)
    this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)

    this.element.addEventListener("toggle", this.sync)
    // `pointerdown`, not `click`: the menu should be gone by the time the
    // press completes, and a click listener fires too late to feel immediate.
    // Listening on the document rather than the window so a handler that stops
    // propagation inside the menu still keeps it open.
    document.addEventListener("pointerdown", this.closeOnOutsideClick)
    document.addEventListener("keydown", this.closeOnEscape)
    this.sync()
  }

  disconnect() {
    this.element.removeEventListener("toggle", this.sync)
    document.removeEventListener("pointerdown", this.closeOnOutsideClick)
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  sync() {
    const summary = this.summaryElement()
    if (!summary) return

    summary.setAttribute("aria-expanded", this.element.open ? "true" : "false")
  }

  closeOnOutsideClick(event) {
    if (!this.element.open) return
    if (this.element.contains(event.target)) return

    this.element.open = false
  }

  // Escape returns focus to the control that opened the menu. Closing a menu
  // and dropping focus to the top of the document is how a keyboard user loses
  // their place on the page.
  closeOnEscape(event) {
    if (event.key !== "Escape") return
    if (!this.element.open) return

    this.element.open = false
    this.summaryElement()?.focus()
  }

  summaryElement() {
    return this.hasSummaryTarget ? this.summaryTarget : this.element.querySelector("summary")
  }
}
